import {
  Breadcrumbs,
  Card,
  CardContent,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getPropertiesByUniqueID } from "../../api/literatureDatabase";

type CompositionProperties = {
  uid: string;
  composition: string;
  doi: string;
  typesOfPhases: string[];
  density: number;
  tensileStrengthRT: number;
  tensileStrengthHT: number;
  atomicComposition: Record<string, number>;
  ductility: number;
};

const CompositionPropertyLabels: Record<keyof CompositionProperties, string> = {
  uid: "Unique ID",
  composition: "Composition",
  doi: "DOI",
  typesOfPhases: "Types of Phases",
  density: "Density",
  tensileStrengthRT: "Tensile Strength (at 23 C)",
  tensileStrengthHT: "Tensile Strength (at 1000 C)",
  atomicComposition: "Atomic Composition",
  ductility: "Ductility",
};

export default function Composition() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<CompositionProperties>();
  const [references, setReferences] = useState<string[]>();
  const [isReferencesLoading, setReferencesLoading] = useState<boolean>();

  useEffect(() => {
    (async () => {
      if (id) {
        setData((await getPropertiesByUniqueID(id)).data?.data);
      }
      if (data?.doi) {
        setReferencesLoading(true);
        setReferences([
          await (
            await fetch(`https://doi.org/${encodeURIComponent(data?.doi)}`, {
              headers: {
                Accept: "text/x-bibliography; style=apa",
              },
            })
          ).text(),
        ]);
        setReferencesLoading(false);
      }
    })();
  }, [data?.doi, id]);

  return (
    <Stack
      direction={"column"}
      sx={{
        backgroundColor: "#f9f9f9",
      }}
      padding={3}
      gap={2}
    >
      <Breadcrumbs aria-label="breadcrumb">
        <Link
          underline="hover"
          color="inherit"
          onClick={() => navigate("/")}
          sx={{ cursor: "pointer" }}
        >
          Home
        </Link>
        <Typography color="text.primary">Compositions</Typography>
        <Typography color="text.primary">{id}</Typography>
      </Breadcrumbs>
      <Typography variant="h4">{data?.composition}</Typography>
      <Stack>
        <Typography gutterBottom variant="h5">
          Composition Properties
        </Typography>
        <Card>
          <CardContent>
            <Stack gap={2}>
              <Stack direction={"row"} gap={2}>
                <span className="w-20 h-20 border p-2 flex items-center justify-center">
                  {/* TODO: Implement composition figure */}
                  [Figure]
                </span>
              </Stack>
              {data ? (
                <table className="w-fit">
                  <tbody>
                    {Object.keys(data).map((k) => {
                      const key = k as keyof CompositionProperties;
                      return (
                        <tr>
                          <th align="left" className="pr-2">
                            {CompositionPropertyLabels[key]}
                          </th>
                          <td>
                            {typeof data[key] == "object"
                              ? // Atomic composition
                                Object.entries(data[key])
                                  .map(([k, v]) => `${k}: ${v}`)
                                  .join(", ")
                              : Array.isArray(data[key])
                                ? // Types of phases
                                  data[key].join(", ")
                                : // Rest of the properties
                                  data[key]}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                "Fetching properties..."
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>
      <Stack>
        <Typography gutterBottom variant="h5">
          Literature References
        </Typography>
        <Card>
          <CardContent>
            <ol className="list-decimal list-inside">
              {isReferencesLoading
                ? "Fetching references..."
                : references?.map((r) => <li>{r}</li>)}
            </ol>
          </CardContent>
        </Card>
      </Stack>
    </Stack>
  );
}
